import { type FunctionComponent } from 'react';
import { type Maybe, type ProductViewEnum } from '../../../__generated__/graphql-client-types';
import { PlaceholderImage } from '../../images/placeholder-image/placeholder-image.component';
import { cardShadow } from '../search-product-card/search-product-card.css';

export type SearchProductCardSkeletonProps = {
	cardIndex: number;
};

const SwatchesSkeleton: FunctionComponent = () => (
	<div className="flex mb2">
		<PlaceholderImage className="br2 mr1" width={32} height={32} />
		<PlaceholderImage className="br2 mr1" width={32} height={32} />
		<PlaceholderImage className="br2 mr1" width={32} height={32} />
	</div>
);

const TitleSkeleton: FunctionComponent = () => (
	<>
		<PlaceholderImage className="br2 mb1 w-100" width={100} height={16} />
		<PlaceholderImage className="br2 mb1 w-90" height={16} />
		<PlaceholderImage className="br2 mb2 w-40" height={16} />
	</>
);

/** Tile view skeleton - displays as a card in a grid layout */
export const SearchProductCardSkeleton: FunctionComponent<SearchProductCardSkeletonProps> = ({ cardIndex }) => (
	<div
		className={`bg-theme-white bb-ns b--theme-grey-light w-50 w-25-ns pv1 ${cardIndex % 2 === 0 ? 'pr1' : 'pl1'} pa2-ns mb3-ns`}
		data-testid="search-product-card-skeleton">
		<div className={`flex flex-column pa2 pa0-ns ba b--theme-grey-light br2 b--none-ns ${cardShadow}`}>
			<PlaceholderImage width={214} height={214} bypassLazyLoad />
			<div className="pt2">
				{/* Swatches */}
				<SwatchesSkeleton />
				{/* Title and details */}
				<div className="flex flex-column">
					<TitleSkeleton />
					<PlaceholderImage className="br2 mb2" width={120} height={14} />
					<PlaceholderImage className="br2 mb2" width={100} height={14} />
					<PlaceholderImage className="br2 mb2" width={80} height={20} />
					<PlaceholderImage className="br2 mb2" width={150} height={14} />
				</div>
			</div>
		</div>
	</div>
);

/** List view skeleton - displays as a horizontal row with image, details, and attributes */
export const SearchProductCardListSkeleton: FunctionComponent = () => (
	<div
		className="bg-theme-white bb-ns b--theme-grey-light mt2 mt3-ns ba bl-0-ns br-0-ns bt-0-ns br2 br0-ns w-100"
		data-testid="search-product-card-list-skeleton">
		<div className="flex w-100">
			{/* Image section */}
			<div className="w-50 w-25-ns pa2 tc">
				<PlaceholderImage width={214} height={214} bypassLazyLoad />
				{/* Swatches - mobile only */}
				<div className="mt2 dn-ns">
					<SwatchesSkeleton />
					<PlaceholderImage className="dn-ns db br2 mt1" width={120} height={12} />
				</div>
				{/* Compare button - desktop only */}
				<div className="dn db-ns mt2">
					<PlaceholderImage className="br2 center" width={80} height={16} />
				</div>
			</div>

			{/* Details section */}
			<div className="w-50 pa2 flex flex-column">
				{/* Swatches - desktop only */}
				<div className="dn db-ns mb2">
					<SwatchesSkeleton />
				</div>
				<TitleSkeleton />
				<PlaceholderImage className="br2 mb2" width={120} height={14} />
				<PlaceholderImage className="br2 mb2" width={90} height={14} />
				<PlaceholderImage className="br2 mb2" width={80} height={20} />
				<PlaceholderImage className="br2 mb2" width={150} height={14} />
				{/* Add to Cart button - desktop only */}
				<div className="dn db-ns mt3">
					<PlaceholderImage className="br2" width={200} height={40} />
				</div>
			</div>

			{/* Attributes section - desktop only */}
			<div className="dn db-ns w-25 pa2 bg-theme-grey-lighter">
				<PlaceholderImage className="br2 mb2" width={140} height={14} />
				<PlaceholderImage className="br2 mb2" width={120} height={14} />
				<PlaceholderImage className="br2 mb2" width={160} height={14} />
				<PlaceholderImage className="br2 mb2" width={130} height={14} />
				<PlaceholderImage className="br2 mb2" width={150} height={14} />
			</div>
		</div>

		{/* Mobile Add to Cart and Compare */}
		<div className="dn-ns pa2 tc flex flex-column">
			<PlaceholderImage className="br2 w-100 mw5 center" height={40} />
			<PlaceholderImage className="br2 mt2 center" width={80} height={16} />
		</div>
	</div>
);

export type SearchProductCardSkeletonListProps = {
	count?: number;
	viewType?: Maybe<ProductViewEnum>;
};

export const SearchProductCardSkeletonList: FunctionComponent<SearchProductCardSkeletonListProps> = ({ count = 8, viewType = 'TILE' }) => {
	const isListView = viewType === 'LIST';

	return (
		<div
			className={`flex ${isListView ? 'flex-column flex-row-ns' : ''} flex-wrap ml3-l`}
			data-testid="search-product-card-skeleton-list">
			{Array.from({ length: count }).map((_, index) =>
				isListView ? <SearchProductCardListSkeleton key={index} /> : <SearchProductCardSkeleton key={index} cardIndex={index} />
			)}
		</div>
	);
};
